import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { navLinks } from '../mock';
import { Menu, X, ArrowUpRight } from 'lucide-react';

const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);
  const onScroll = useCallback(() => setScrolled(window.scrollY > 12), []);
  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);
  return scrolled;
};

const DesktopNav = ({ goTo }) => (
  <>
    <nav className="hidden md:flex items-center gap-9">
      {navLinks.map((l) => (
        <button
          key={l.href}
          onClick={() => goTo(l.href)}
          className="text-[13.5px] font-medium tracking-wide text-slate-600 hover:text-[#0B2545] transition-colors"
        >
          {l.label}
        </button>
      ))}
    </nav>
    <div className="hidden md:flex items-center">
      <button
        onClick={() => goTo('#contact')}
        className="group inline-flex items-center gap-1.5 rounded-full bg-[#0B2545] hover:bg-[#13315C] text-white text-[13px] font-medium px-4 py-2 transition-all shadow-sm hover:shadow-md"
        data-testid="header-request-info-btn"
      >
        Request Info
        <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
    </div>
  </>
);

const MobileMenu = ({ goTo }) => (
  <div className="md:hidden border-t border-slate-200/70 bg-white">
    <div className="px-5 py-4 flex flex-col gap-1">
      {navLinks.map((l) => (
        <button
          key={l.href}
          onClick={() => goTo(l.href)}
          className="text-left py-2.5 text-[15px] font-medium text-slate-700"
        >
          {l.label}
        </button>
      ))}
      <button
        onClick={() => goTo('#contact')}
        className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0B2545] text-white text-[14px] font-medium px-4 py-2.5"
      >
        Request Info <ArrowUpRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const Header = () => {
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goTo = (href) => {
    setOpen(false);
    if (location.pathname !== '/') {
      navigate(`/${href}`);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-md bg-white/75 border-b border-slate-200/70'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/">
          <Logo />
        </Link>
        <DesktopNav goTo={goTo} />
        <button
          className="md:hidden p-2 -mr-2 text-[#0B2545]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && <MobileMenu goTo={goTo} />}
    </header>
  );
};

export default Header;
